// frontend/src/components/legal/LegalConsentPopup.jsx
// Non-blocking banner shown when the user has not accepted the latest legal
// documents. Reused consent component used by App.jsx after login.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, FileText } from "lucide-react";
import { recordConsent } from "../../services/legal";
import "./LegalConsentPopup.css";

function LegalConsentPopup({ version, summary, effectiveDate, onAccepted, onDismiss }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleReview = () => {
    if (onDismiss) onDismiss();
    navigate("/legal");
  };

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await recordConsent(version);
      onAccepted(version);
    } catch (err) {
      setError(err.message || "Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="legal-consent-popup" role="dialog" aria-live="polite" aria-label="Legal documents update">
      <div className="legal-consent-popup-content">
        <div className="legal-consent-popup-header">
          <div className="legal-consent-popup-icon">
            <FileText className="h-5 w-5" />
          </div>
          <div className="legal-consent-popup-title-block">
            <h2 className="legal-consent-popup-title">Updated legal documents</h2>
            {effectiveDate && (
              <p className="legal-consent-popup-date">Effective {effectiveDate}</p>
            )}
          </div>
          {onDismiss && (
            <button
              type="button"
              className="legal-consent-popup-close"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <p className="legal-consent-popup-summary">
          {summary || "We have updated our legal documents. Please review and accept them to continue using Pista."}
        </p>

        {error && <p className="legal-consent-popup-error">{error}</p>}

        <div className="legal-consent-popup-actions">
          <button
            type="button"
            className="legal-consent-popup-button legal-consent-popup-button-secondary"
            onClick={handleReview}
            disabled={submitting}
          >
            Review documents
          </button>
          <button
            type="button"
            className="legal-consent-popup-button legal-consent-popup-button-primary"
            onClick={handleAccept}
            disabled={submitting}
          >
            {submitting ? "Accepting..." : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LegalConsentPopup;
